import { useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

export interface PartenaireConfig {
  id: number;
  code: string;
  nom: string;
  description: string | null;
  discord_link: string;
  color: string;
  icon: string | null;
  ordre: number;
  actif: number;
  created_at?: string;
}

export const PARTENAIRE_COLORS = ['blue', 'red', 'green', 'purple', 'yellow', 'orange'] as const;
export type PartenaireColor = typeof PARTENAIRE_COLORS[number];

const emptyPartnerForm = {
  code: '',
  nom: '',
  description: '',
  discord_link: '',
  color: 'blue' as PartenaireColor,
  icon: '',
  ordre: 0,
  actif: 1,
};

interface UsePartenairesConfigParams {
  setError: (msg: string | null) => void;
  setSuccess: (msg: string | null) => void;
}

export function usePartenairesConfig({
  setError,
  setSuccess,
}: UsePartenairesConfigParams) {
  const [partenairesConfig, setPartenairesConfig] = useState<PartenaireConfig[]>([]);
  const [partnerForm, setPartnerForm] = useState<typeof emptyPartnerForm>(emptyPartnerForm);
  const [editingPartnerId, setEditingPartnerId] = useState<number | null>(null);
  const [savingPartner, setSavingPartner] = useState(false);

  const handleEditPartner = (p: PartenaireConfig) => {
    setEditingPartnerId(p.id);
    setPartnerForm({
      code: p.code,
      nom: p.nom,
      description: p.description || '',
      discord_link: p.discord_link,
      color: (PARTENAIRE_COLORS.includes(p.color as PartenaireColor) ? p.color : 'blue') as PartenaireColor,
      icon: p.icon || '',
      ordre: p.ordre,
      actif: p.actif,
    });
  };

  const handleCancelEditPartner = () => {
    setEditingPartnerId(null);
    setPartnerForm({ ...emptyPartnerForm });
  };

  const handleSavePartner = async () => {
    const nom = partnerForm.nom.trim();
    const discord_link = partnerForm.discord_link.trim();
    if (!nom || !discord_link) {
      setError('Nom et lien Discord sont requis.');
      return;
    }
    setError(null);
    setSavingPartner(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiBaseUrl();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const body = {
        code: partnerForm.code.trim() || nom.toLowerCase().replace(/\s+/g, '-'),
        nom,
        description: partnerForm.description.trim() || null,
        discord_link,
        color: partnerForm.color,
        icon: partnerForm.icon.trim() || null,
        ordre: partnerForm.ordre,
        actif: partnerForm.actif ? 1 : 0,
      };
      if (editingPartnerId) {
        const res = await fetch(`${apiUrl}/api/config/partenaires/${editingPartnerId}`, {
          method: 'PUT',
          headers,
          credentials: 'include',
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.success && data.data) {
          setPartenairesConfig((prev) => prev.map((x) => (x.id === editingPartnerId ? data.data : x)));
          setSuccess('Partenaire mis à jour.');
          handleCancelEditPartner();
        } else {
          setError(data.error || 'Erreur lors de la mise à jour.');
        }
      } else {
        const res = await fetch(`${apiUrl}/api/config/partenaires`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (data.success && data.data) {
          setPartenairesConfig((prev) => [...prev, data.data]);
          setSuccess('Partenaire ajouté.');
          handleCancelEditPartner();
        } else {
          setError(data.error || 'Erreur lors de l\'ajout.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erreur.');
    } finally {
      setSavingPartner(false);
    }
  };

  const handleDeletePartner = async (id: number) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = getApiBaseUrl();
      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${apiUrl}/api/config/partenaires/${id}`, {
        method: 'DELETE',
        headers,
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        setPartenairesConfig((prev) => prev.filter((x) => x.id !== id));
        setSuccess('Partenaire supprimé.');
        if (editingPartnerId === id) handleCancelEditPartner();
      } else {
        setError(data.error || 'Erreur lors de la suppression.');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur.');
    }
  };

  return {
    partenairesConfig,
    setPartenairesConfig,
    partnerForm,
    setPartnerForm,
    editingPartnerId,
    savingPartner,
    handleEditPartner,
    handleCancelEditPartner,
    handleSavePartner,
    handleDeletePartner,
  };
}
